import {expensePolicyStatusConstants} from '../../constants/expenses.constants';

const toCents = (value) => Math.round((Number(value) || 0) * 100);

const hasValue = (value) => value !== null && value !== undefined && value !== '';

/**
 * Adds amounts up per currency, in whole cents so 200 rows do not drift by float error.
 * @param {Array<{currency: string, amount: number}>} entries - Amounts in major units with their currency.
 * @returns {Array<{currency: string, amount: number}>} One entry per currency, in first-seen order.
 */
const sumByCurrency = (entries) => {
  const cents = new Map();
  entries.forEach(({currency, amount}) => {
    cents.set(currency, (cents.get(currency) ?? 0) + toCents(amount));
  });
  return [...cents].map(([currency, total]) => ({currency, amount: total / 100}));
};

export const bulkApproveHelpers = {
  /**
   * True when the expense needs a decision before bulk approval: its status is out-of-policy and its price is
   * above the stored covered amount. With no covered amount the expense is out of policy for another reason
   * (missing receipt, budget, no computable cap), not because of its amount, so it is not outside the policy.
   * @param {Object} expense - Expense; `price` is a string, `policy_covered_amount` a number or null.
   * @returns {boolean} True if the expense is outside the policy.
   * @example
   * bulkApproveHelpers.isAmountOutsidePolicy({policy_status: 'out_of_policy', price: '100', policy_covered_amount: 70}); // true
   * bulkApproveHelpers.isAmountOutsidePolicy({policy_status: 'out_of_policy', price: '100', policy_covered_amount: null}); // false
   */
  isAmountOutsidePolicy: (expense) => {
    if (expense?.policy_status !== expensePolicyStatusConstants.OUT_OF_POLICY) return false;
    if (!hasValue(expense.policy_covered_amount)) return false;
    return toCents(expense.price) > toCents(expense.policy_covered_amount);
  },

  /**
   * How much the expense is over the policy amount, in major units. Zero when it is not outside the policy.
   * @param {Object} expense - Expense with price and policy_covered_amount.
   * @returns {number} Overage rounded to cents.
   * @example
   * bulkApproveHelpers.getOverage({policy_status: 'out_of_policy', price: '100', policy_covered_amount: 70}); // 30
   * bulkApproveHelpers.getOverage({policy_status: 'out_of_policy', price: '100', policy_covered_amount: null}); // 0
   */
  getOverage: (expense) => {
    if (!bulkApproveHelpers.isAmountOutsidePolicy(expense)) return 0;
    return (toCents(expense.price) - toCents(expense.policy_covered_amount)) / 100;
  },

  /**
   * The policy amount to cap an approval at: the stored covered amount, but only when the expense is
   * outside the policy by it. In-policy expenses and ones priced at or below the amount have no cap here.
   * @param {Object} expense - Expense with policy_status, price and policy_covered_amount.
   * @returns {number|null} The covered amount, or null when there is no cap to apply.
   * @example
   * bulkApproveHelpers.getPolicyCap({policy_status: 'out_of_policy', price: '100', policy_covered_amount: 70}); // 70
   * bulkApproveHelpers.getPolicyCap({policy_status: null, price: '50', policy_covered_amount: 70}); // null
   */
  getPolicyCap: (expense) => {
    if (!bulkApproveHelpers.isAmountOutsidePolicy(expense)) return null;
    return hasValue(expense.policy_covered_amount) ? Number(expense.policy_covered_amount) : null;
  },

  /**
   * Splits a selection into the expenses that can be approved at once and those that need a decision.
   * Out-of-policy ones are sorted by overage, highest first; equal overage keeps the selection order.
   * Totals are grouped by currency because nothing here converts between currencies.
   * @param {Array<Object>} expenses - Selected expenses.
   * @returns {{inPolicy: Object[], outOfPolicy: Object[], inPolicyTotals: Array<{currency: string, amount: number}>, allTotals: Array<{currency: string, amount: number}>, outOfPolicyTotals: Array<{currency: string, amount: number}>, overageTotals: Array<{currency: string, amount: number}>}} The split and its totals.
   * @example
   * bulkApproveHelpers.splitSelection([inPolicyExpense, outOfPolicyExpense]).outOfPolicy.length; // 1
   */
  splitSelection: (expenses = []) => {
    const inPolicy = [];
    const outOfPolicy = [];
    expenses.forEach(expense => {
      (bulkApproveHelpers.isAmountOutsidePolicy(expense) ? outOfPolicy : inPolicy).push(expense);
    });
    const sortedOutOfPolicy = outOfPolicy
      .map((expense, index) => ({expense, index, overage: bulkApproveHelpers.getOverage(expense)}))
      .sort((a, b) => b.overage - a.overage || a.index - b.index);

    const priceOf = ({currency, price}) => ({currency: currency || '', amount: Number(price) || 0});
    return {
      inPolicy,
      outOfPolicy: sortedOutOfPolicy.map(({expense}) => expense),
      inPolicyTotals: sumByCurrency(inPolicy.map(priceOf)),
      allTotals: sumByCurrency(expenses.map(priceOf)),
      outOfPolicyTotals: sumByCurrency(outOfPolicy.map(priceOf)),
      overageTotals: sumByCurrency(
        sortedOutOfPolicy
          .map(({expense, overage}) => ({currency: expense.currency || '', amount: overage}))
      ),
    };
  },
};
